// src/pages/Admin/AdminRoles/AdminRoles.jsx
import React, { useState, useEffect, useContext } from 'react';
import AdminSidebar from '../../../components/AdminSidebar/AdminSidebar';
import Modal from '../../../components/Modal/Modal';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { AuthContext } from '../../../context/AuthContext';
import './AdminRoles.css';

// Cambia la URL base de la API a relativa para producción
// Opción B: permite apuntar a API remota con REACT_APP_API_BASE_URL en desarrollo
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

const AdminRoles = () => {
  const { authToken } = useContext(AuthContext);
  const [roles, setRoles] = useState([]);
  const [isAddRoleModalOpen, setIsAddRoleModalOpen] = useState(false);
  const [isEditRoleModalOpen, setIsEditRoleModalOpen] = useState(false);
  const [currentRole, setCurrentRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE_URL}/roles`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }
      const data = await response.json();
      setRoles(data);
    } catch (err) {
      setError('Error al cargar roles: ' + err.message);
      console.error('Error fetching roles:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const handleAddClick = () => {
    setCurrentRole({ name: '', permissions: '' });
    setIsAddRoleModalOpen(true);
  };

  const handleEditClick = (role) => {
    const permissionsArray = Array.isArray(role.permissions)
      ? role.permissions
      : (role.permissions ? role.permissions.split(',').map(p => p.trim()).filter(Boolean) : []);
    setCurrentRole({ ...role, permissions: permissionsArray.join(', ') });
    setIsEditRoleModalOpen(true);
  };

  const handleDeleteClick = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este rol?')) {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/roles/${id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        setRoles(roles.filter((role) => role.id !== id));
      } catch (err) {
        setError('Error al eliminar rol: ' + err.message);
        console.error('Error deleting role:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentRole((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddRole = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const roleToSend = {
        ...currentRole,
        permissions: currentRole.permissions
          ? currentRole.permissions
              .split(',')
              .map(p => p.trim())
              .filter(Boolean)
          : [],
      };
      const response = await fetch(`${API_BASE_URL}/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(roleToSend),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      fetchRoles();
      closeModals();
    } catch (err) {
      setError('Error al añadir rol: ' + err.message);
      console.error('Error adding role:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const roleToSend = {
        ...currentRole,
        permissions: currentRole.permissions
          ? currentRole.permissions
              .split(',')
              .map(p => p.trim())
              .filter(Boolean)
          : [],
      };
      const response = await fetch(`${API_BASE_URL}/roles/${currentRole.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(roleToSend),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      fetchRoles();
      closeModals();
    } catch (err) {
      setError('Error al actualizar rol: ' + err.message);
      console.error('Error updating role:', err);
    } finally {
      setLoading(false);
    }
  };

  const closeModals = () => {
    setIsAddRoleModalOpen(false);
    setIsEditRoleModalOpen(false);
    setCurrentRole(null);
    setError(null);
  };

  const filteredRoles = roles.filter(
    (role) =>
      role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (Array.isArray(role.permissions) ? role.permissions.join(', ').toLowerCase() : '').includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredRoles.length / perPage) || 1;
  const paginatedRoles = filteredRoles.slice((page - 1) * perPage, page * perPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
  };

  return (
    <div className="admin-roles-container">
      <AdminSidebar />
      <div className="admin-content">
        <h1 className="admin-page-title">Administración de Roles</h1>
        <div style={{display:'flex',gap:16,alignItems:'center',marginBottom:16,flexWrap:'wrap'}}>
          <label style={{fontWeight:500}}>Ver como:</label>
          <select value={viewMode} onChange={e => setViewMode(e.target.value)} style={{padding:4}}>
            <option value="table">Tabla</option>
            <option value="cards">Tarjetas</option>
          </select>
          <label style={{fontWeight:500}}>Mostrar:</label>
          <select value={perPage} onChange={e => {setPerPage(Number(e.target.value));setPage(1);}} style={{padding:4}}>
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
          <input
            type="text"
            placeholder="Buscar por nombre o permisos..."
            value={searchTerm}
            onChange={e => {setSearchTerm(e.target.value); setPage(1);}}
            className="search-input"
            style={{minWidth:180}}
          />
          <span style={{color:'#888',fontSize:'0.95rem'}}>Página {page} de {totalPages}</span>
        </div>
        {error && <p className="error-message">{error}</p>}
        {loading ? (
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',margin:'40px 0'}}>
            <div className="modern-spinner" />
            <span style={{color:'#888',fontWeight:500,fontSize:'1.1rem',marginTop:12}}>Cargando roles...</span>
          </div>
        ) : (
          <div className="admin-section">
            <h2 className="section-title">Lista de Roles</h2>
            {viewMode === 'table' ? (
              <div className="table-responsive">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Nombre</th>
                      <th>Permisos</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedRoles.length > 0 ? (
                      paginatedRoles.map((role) => (
                        <tr key={role.id}>
                          <td>{role.id}</td>
                          <td>{role.name}</td>
                          <td>{Array.isArray(role.permissions) ? role.permissions.join(', ') : role.permissions}</td>
                          <td>
                            <div className="action-buttons">
                              <button onClick={() => handleEditClick(role)} className="action-button icon-button edit-button"><FaEdit /></button>
                              <button onClick={() => handleDeleteClick(role.id)} className="action-button icon-button delete-button"><FaTrash /></button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="no-results">No se encontraron roles.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{display:'flex',flexWrap:'wrap',gap:20,marginTop:16}}>
                {paginatedRoles.length > 0 ? (
                  paginatedRoles.map(role => (
                    <div key={role.id} style={{background:'#23272f',borderRadius:10,boxShadow:'0 2px 8px 0 rgba(0,0,0,0.07)',padding:18,minWidth:260,maxWidth:340,flex:'1 1 260px',borderLeft:'5px solid #FFD700',color:'#fff'}}>
                      <div style={{fontWeight:700,fontSize:'1.05rem',marginBottom:6}}>{role.name}</div>
                      <div style={{marginBottom:4}}><b>Permisos:</b> {Array.isArray(role.permissions) ? role.permissions.join(', ') : role.permissions}</div>
                      <div style={{color:'#bbb',fontSize:'0.9rem'}}>ID: {role.id}</div>
                      <div style={{display:'flex',gap:8,marginTop:10}}>
                        <button onClick={() => handleEditClick(role)} className="action-button icon-button edit-button"><FaEdit /></button>
                        <button onClick={() => handleDeleteClick(role.id)} className="action-button icon-button delete-button"><FaTrash /></button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{width:'100%',textAlign:'center',color:'#888',padding:'40px 0'}}>No se encontraron roles.</div>
                )}
              </div>
            )}
            {/* Paginación */}
            {filteredRoles.length > 0 && (
              <div style={{display:'flex',gap:8,justifyContent:'center',alignItems:'center',margin:'32px 0'}}>
                <button onClick={()=>handlePageChange(page-1)} disabled={page===1} style={{padding:'6px 14px',borderRadius:6,border:'1px solid #FFD700',background:page===1?'#eee':'#FFD700',color:page===1?'#aaa':'#222',fontWeight:600,cursor:page===1?'not-allowed':'pointer'}}>Anterior</button>
                <span style={{fontWeight:500}}>Página {page} de {totalPages}</span>
                <button onClick={()=>handlePageChange(page+1)} disabled={page===totalPages} style={{padding:'6px 14px',borderRadius:6,border:'1px solid #FFD700',background:page===totalPages?'#eee':'#FFD700',color:page===totalPages?'#aaa':'#222',fontWeight:600,cursor:page===totalPages?'not-allowed':'pointer'}}>Siguiente</button>
              </div>
            )}
          </div>
        )}

        {/* Modal para Añadir Nuevo Rol */}
        {isAddRoleModalOpen && (
          <Modal onClose={closeModals}>
            <h2 className="modal-title">Añadir Nuevo Rol</h2>
            <form onSubmit={handleAddRole} className="modal-form">
              <div className="form-group">
                <label htmlFor="newRoleName">Nombre del Rol:</label>
                <input
                  type="text"
                  id="newRoleName"
                  name="name"
                  value={currentRole?.name || ''}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="newRolePermissions">
                  Permisos (separados por coma, ej: ver_logs, ban):
                </label>
                <input
                  type="text"
                  id="newRolePermissions"
                  name="permissions"
                  value={currentRole?.permissions || ''}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="action-button primary-button">
                  Añadir Rol
                </button>
                <button
                  type="button"
                  className="action-button secondary-button"
                  onClick={closeModals}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </Modal>
        )}

        {/* Modal para Editar Rol */}
        {isEditRoleModalOpen && currentRole && (
          <Modal onClose={closeModals}>
            <h2 className="modal-title">Editar Rol</h2>
            <form onSubmit={handleUpdateRole} className="modal-form">
              <div className="form-group">
                <label htmlFor="editRoleId">ID del Rol:</label>
                <input
                  type="text"
                  id="editRoleId"
                  name="id"
                  value={currentRole.id || ''}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label htmlFor="editRoleName">Nombre del Rol:</label>
                <input
                  type="text"
                  id="editRoleName"
                  name="name"
                  value={currentRole.name || ''}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="editRolePermissions">
                  Permisos (separados por coma, ej: ver_logs, ban):
                </label>
                <input
                  type="text"
                  id="editRolePermissions"
                  name="permissions"
                  value={currentRole.permissions || ''}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="action-button primary-button">
                  Guardar Cambios
                </button>
                <button
                  type="button"
                  className="action-button secondary-button"
                  onClick={closeModals}
                >
                  Cancelar
                </button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default AdminRoles;